/**
 * Project Management Service
 * AIDLC Administrative Backend
 *
 * API calls for project health, agent allocation, and cross-project overview.
 * Extends the existing projectService with management-specific endpoints.
 */

import apiClient from './api';
import type { ProjectOverview, ProjectHealthData, AllocationEntry } from '../types/projects';

const PROJECTS_PATH = '/projects';

/**
 * Fetch calculated health data for a project
 */
export async function fetchProjectHealth(projectId: string): Promise<ProjectHealthData> {
  const response = await apiClient.get<ProjectHealthData>(`${PROJECTS_PATH}/${projectId}/health`);
  return response.data;
}

/**
 * Fetch agents assigned to a project
 */
export async function fetchProjectAgents(projectId: string): Promise<string[]> {
  const response = await apiClient.get<Array<{ id: string }>>(`${PROJECTS_PATH}/${projectId}/agents`);
  return response.data.map((a) => a.id);
}

/**
 * Update agent assignments for a project
 */
export async function updateProjectAgents(projectId: string, agentIds: string[]): Promise<void> {
  await apiClient.put(`${PROJECTS_PATH}/${projectId}/agents`, { agentIds });
}

/**
 * Fetch cross-project agent allocation matrix
 */
export async function fetchAllocationMatrix(): Promise<AllocationEntry[]> {
  const response = await apiClient.get<AllocationEntry[]>(`${PROJECTS_PATH}/allocation-matrix`);
  return response.data;
}

/**
 * Fetch all projects with health summary for overview cockpit
 */
export async function fetchProjectsOverview(): Promise<ProjectOverview[]> {
  const response = await apiClient.get<ProjectOverview[]>(`${PROJECTS_PATH}/overview`);
  return response.data;
}
